import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { BehaviorSubject, catchError, combineLatest, map, Observable, of, shareReplay, startWith, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OrganisationsService } from '../../services/organisations.service';
import { NewOrganisationPick } from '../../models/organisations';
import { AssignableParty } from '../../../contacts/components/add-department-contact/models/assignable-party';
import { ContactsService } from '../../../contacts/services/contacts.service';
import { Contact } from '../../../contacts/models/contacts';

// 8px+8px padding + ~16px line-height for the 13px row text — see .add-org-popover__sugg.
export const UNASSIGNED_PARTY_ROW_HEIGHT = 32;

// Minimum characters before the (client-side) search filters the list. Below this, the list is
// left exactly as it was — not cleared — so an empty search shows the full list.
const MIN_SEARCH_LENGTH = 1;

// Anchored popover for picking a not-yet-listed CRM party — see organisations-design-spec.md §1.2.
// Exclusion of already-assigned organisations is server-side, via
// GET /supportContacts/organisations/unassigned. That response is ~3.1MB, so OrganisationsService
// caches it and OrganisationsListComponent prefetches it in parallel with the grid's own load —
// by the time this popover opens, getUnassigned() below usually just replays the cached result
// instead of starting a fresh download. Filtered/virtual-scrolled entirely client-side, the same
// fetch-once + local-filter pattern AddPersonContactComponent uses for its eligible-users list.
@Component({
  selector: 'app-add-organisation-popover',
  standalone: true,
  imports: [AsyncPipe, ReactiveFormsModule, MatIcon, MatProgressSpinner, ScrollingModule],
  templateUrl: './add-organisation-popover.component.html',
  styleUrl: './add-organisation-popover.component.scss'
})
export class AddOrganisationPopoverComponent implements OnInit {
  // Viewport-relative pixel coordinates from the trigger button's getBoundingClientRect(),
  // computed by OrganisationsListComponent.toggleAddOrg() — see that method for why this is
  // `position: fixed` rather than the plain CSS-anchored `position: absolute` this used to be.
  @Input() anchorTop = 0;
  @Input() anchorRight = 0;
  @HostBinding('style.top.px') get top() {
    return this.anchorTop;
  }
  @HostBinding('style.right.px') get right() {
    return this.anchorRight;
  }

  @Output() picked = new EventEmitter<NewOrganisationPick>();
  @Output() closed = new EventEmitter<void>();

  protected readonly rowHeight = UNASSIGNED_PARTY_ROW_HEIGHT;

  // Starts disabled so the admin can't type a search before the party list has loaded.
  searchControl = new FormControl({ value: '', disabled: true }, { nonNullable: true });
  loading = true;
  hasError = false;
  filteredParties$ = of<AssignableParty[]>([]);

  // Reused across the whole sort — constructing an Intl.Collator per comparison (e.g. passing
  // options directly to localeCompare) is ~25x slower at this list's size (130k+ rows).
  private static readonly nameCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

  private allParties$ = new BehaviorSubject<AssignableParty[]>([]);
  private organisationsService = inject(OrganisationsService);
  private contactsService = inject(ContactsService);
  private destroyRef = inject(DestroyRef);
  private elementRef = inject(ElementRef<HTMLElement>);

  // Kicked off alongside getUnassigned() below (not on `select()`) so the new organisation's
  // People picker is already loaded — or loading — by the time the admin has searched/picked a
  // party, instead of only starting once OrganisationDetailComponent mounts. shareReplay(1) with
  // refCount false keeps the single response cached for whichever party ends up picked, without
  // re-fetching if the popover itself is closed and reopened before a pick is made. Existing
  // organisations are unaffected — their detail view still fetches contacts fresh on its own.
  // catchError sits before shareReplay so a failure is normalized to [] once, for every subscriber
  // (this component's own eager subscribe below, and later OrganisationDetailComponent) — otherwise
  // the raw error would replay to whichever of those subscribes last, and the detail component's
  // subscribe(fn) below has no error handler to catch it.
  private contacts$: Observable<Contact[]> = this.contactsService.getAllContacts().pipe(
    map(response => response.data),
    catchError(() => of<Contact[]>([])),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  ngOnInit() {
    this.organisationsService
      .getUnassigned()
      .pipe(
        catchError(() => {
          this.hasError = true;
          return of<AssignableParty[]>([]);
        }),
        tap(parties => {
          const sorted = [...parties].sort((a, b) =>
            AddOrganisationPopoverComponent.nameCollator.compare(a.name, b.name)
          );
          this.allParties$.next(sorted);
          this.loading = false;
          if (!this.hasError) {
            this.searchControl.enable();
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    // contacts$ above is a cold HttpClient observable — shareReplay only caches for whoever
    // subscribes, it doesn't start the request on its own. Subscribing here is what actually fires
    // it now, alongside getUnassigned(), instead of leaving it dormant until select() hands it off
    // and OrganisationDetailComponent becomes its first subscriber.
    this.contacts$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    this.filteredParties$ = combineLatest([this.allParties$, this.searchControl.valueChanges.pipe(startWith(''))]).pipe(
      map(([parties, term]) => {
        const value = term.trim().toLowerCase();
        if (value.length < MIN_SEARCH_LENGTH) {
          return parties;
        }
        return parties.filter(
          party => party.name.toLowerCase().includes(value) || party.number.toLowerCase().includes(value)
        );
      })
    );
  }

  trackById(_: number, party: AssignableParty) {
    return party.id;
  }

  clearSearch() {
    this.searchControl.setValue('');
  }

  select(party: AssignableParty) {
    this.picked.emit({ party, contacts$: this.contacts$ });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.closed.emit();
    }
  }

  // anchorTop/anchorRight are a one-time snapshot of the button's position (see the class-level
  // comment above) — a resize (orientation change, devtools, browser zoom) leaves them stale, so
  // close rather than let the popover drift away from its button.
  @HostListener('window:resize')
  onWindowResize() {
    this.closed.emit();
  }
}
